import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Button, SectionCard } from "@/deps/ui-library";
import { useCompanyProfile, useInvoice, useInvoiceCredits, useIssueInvoice } from "@/api/queries";
import { UseInvoiceCreditDialog } from "./UseInvoiceCreditDialog";
import { useCan, useDocumentMailEnabled } from "@/app/session";
import { useToast } from "@/app/toasts";
import { DraftActions } from "@/components/DraftActions";
import { PrintOverlay } from "@/components/print/PrintOverlay";
import { TaxInvoicePrint } from "@/components/print/TaxInvoicePrint";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { DocumentEmails } from "@/components/DocumentEmails";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { SendEmailDialog } from "@/components/SendEmailDialog";
import { DocumentScreen, LinesTable } from "@/components/DocumentScreen";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { PaymentStatusBadge } from "@/components/list/StatusBadge";
import { calculateTotals } from "@/lib/invoiceTotals";
import { formatDate, dueLabel } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { termsLabel } from "@/lib/paymentTerms";
import { useTaxWords } from "@/app/session";

export function InvoiceDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/sales/invoices/$id" });
  const { data, isPending, error } = useInvoice(id);
  const { data: seller } = useCompanyProfile();
  const issue = useIssueInvoice();
  const can = useCan();
  const navigate = useNavigate();
  const [printing, setPrinting] = useState(false);
  const [usingCredit, setUsingCredit] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const mailEnabled = useDocumentMailEnabled();
  const toast = useToast();
  const { data: credits } = useInvoiceCredits(id, data?.status === "approved");
  const held = useApprovalHoldsDraft("invoice", id, data?.status === "draft");

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading invoice…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That invoice could not be loaded.</p>;

  // Recomputed from the lines rather than read from the header: if the two ever disagree, the
  // screen shows the arithmetic rather than the assertion.
  const totals = calculateTotals(
    data.lines.map((line) => ({
      category: line.vatCategory,
      rate: line.vatRatePct,
      grossPrice: line.unitPrice,
      quantity: line.quantity,
    })),
    [],
    { paid: data.totals.paid },
  );

  return (
    <>
      <PrintOverlay
        open={printing}
        onClose={() => setPrinting(false)}
        title={`Invoice ${data.number ?? ""}`.trim()}
      >
        <TaxInvoicePrint invoice={data} seller={seller} />
      </PrintOverlay>

      {usingCredit ? (
        <UseInvoiceCreditDialog
          invoice={data}
          credits={credits?.available ?? []}
          onClose={() => setUsingCredit(false)}
        />
      ) : null}

      {emailing ? (
        <SendEmailDialog
          target={{ kind: "invoice", id }}
          onClose={() => setEmailing(false)}
          onSent={(recipients) => {
            setEmailing(false);
            toast({
              title: `${data.number ?? "Invoice"} is on its way to ${recipients.join(", ")}.`,
              scheme: "success",
            });
          }}
        />
      ) : null}

      <DocumentScreen
        title="Invoice"
        number={data.number}
        status={data.status}
        locked={data.locked}
        subtitle={`${data.customerName} · dated ${formatDate(data.date)}`}
        actions={
          <span className="flex items-center gap-2">
            {issue.isError ? (
              <span role="alert" className="text-bad-text text-xs">
                {issue.error instanceof Error ? issue.error.message : "That could not be issued."}
              </span>
            ) : null}
            {data.status === "draft" && !held && can("sales.invoices.create") ? (
              <DraftActions
                kind="invoices"
                label="invoice"
                id={id}
                onEdit={() => void navigate({ to: "/sales/invoices/$id/edit", params: { id } })}
                onDeleted={() => void navigate({ to: "/sales/invoices" })}
              />
            ) : null}
            {data.status === "draft" ? (
              <ApprovalActions
                kind="invoice"
                id={id}
                canCreate={can("sales.invoices.create")}
                canIssue={can("sales.invoices.approve")}
                issuing={issue.isPending}
                onIssue={() => issue.mutate(id)}
              />
            ) : null}
            {data.status === "approved" &&
            Number(data.balance) > 0 &&
            can("sales.receipts.create") &&
            (credits?.available.length ?? 0) > 0 ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                onClick={() => setUsingCredit(true)}
              >
                Use credit
              </Button>
            ) : null}
            {data.status === "approved" ? (
              <DownloadPdfButton kind="invoice" id={id} number={data.number} />
            ) : null}
            {mailEnabled && can("sales.invoices.create") ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={data.status !== "approved"}
                title={
                  data.status === "approved"
                    ? undefined
                    : "Email is available once the invoice is issued."
                }
                onClick={() => setEmailing(true)}
              >
                Email invoice
              </Button>
            ) : null}
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
          </span>
        }
        facts={[
          { label: "Customer", value: data.customerName },
          ...(data.fromQuotation
            ? [
                {
                  label: "From quotation",
                  value: (
                    <Link
                      to="/sales/quotations/$id"
                      params={{ id: data.fromQuotation.id }}
                      className="num text-primary underline"
                    >
                      {data.fromQuotation.number}
                    </Link>
                  ),
                },
              ]
            : []),
          { label: "Invoice date", value: formatDate(data.date) },
          {
            label: "Due",
            value: data.due ? `${formatDate(data.due)} · ${dueLabel(data.due)}` : "No due date",
          },
          ...(data.paymentTermsDays === undefined
            ? []
            : [{ label: "Payment terms", value: termsLabel(data.paymentTermsDays) }]),
          ...(data.cashSale
            ? [
                {
                  label: "Cash received",
                  value: (
                    <span>
                      <span className="num">{formatAmount(data.cashSale.amountReceived)}</span>
                      {` · ${data.cashSale.method}`}
                      {data.cashSale.bankAccountName ? ` · ${data.cashSale.bankAccountName}` : ""}
                    </span>
                  ),
                },
                ...(Number(data.cashSale.amountReceived) > Number(data.total)
                  ? [
                      {
                        label: "Change",
                        value: (
                          <span className="num">
                            {formatAmount(
                              (Number(data.cashSale.amountReceived) - Number(data.total)).toFixed(
                                2,
                              ),
                            )}
                          </span>
                        ),
                      },
                    ]
                  : []),
              ]
            : []),
          { label: "Payment", value: <PaymentStatusBadge status={data.payment} /> },
          { label: "Balance", value: <span className="num">{formatAmount(data.balance)}</span> },
        ]}
        history={data.history}
        aside={
          <div className="flex flex-col gap-4">
            {data.payments.length > 0 ? (
              <SectionCard title="Payments">
                <ul className="flex flex-col gap-1.5">
                  {data.payments.map((payment) => (
                    <li
                      key={payment.id}
                      className="flex items-baseline justify-between gap-2 text-xs"
                    >
                      <span className="text-fg">{payment.number}</span>
                      <span className="text-fg-muted">{payment.method}</span>
                      <span className="num text-fg">{formatAmount(payment.amount)}</span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            ) : null}
            {(credits?.used.length ?? 0) > 0 ? (
              <SectionCard title="Credits used">
                <ul className="flex flex-col gap-1.5">
                  {credits!.used.map((credit, index) => (
                    <li
                      key={`${credit.sourceId}-${index}`}
                      className="flex items-baseline justify-between gap-2 text-xs"
                    >
                      <span className="num text-fg">{credit.number}</span>
                      <span className="text-fg-muted">{formatDate(credit.date)}</span>
                      <span className="num text-fg">{formatAmount(credit.amount)}</span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            ) : null}
            <ApprovalCard kind="invoice" id={id} />
            <DocumentEmails of={{ kind: "invoice", id }} />
          </div>
        }
      >
        <SectionCard title="Lines" flush>
          <LinesTable
            caption={`Lines of invoice ${data.number ?? "draft"}`}
            lines={data.lines.map((line) => ({
              n: line.n,
              label: line.itemName,
              account: line.accountName,
              quantity: line.quantity,
              unitPrice: formatAmount(line.unitPrice),
              tax: `${line.vatCategory} · ${line.vatRatePct}%`,
              net: formatAmount(line.lineNet),
            }))}
          />
        </SectionCard>

        <div className="grid gap-4 md:grid-cols-2">
          <SectionCard title={say("VAT breakdown")}>
            <VatBreakdownTable totals={totals} />
          </SectionCard>
          <SectionCard title="Totals">
            <TotalsBlock totals={totals} />
          </SectionCard>
        </div>
      </DocumentScreen>
    </>
  );
}
